/**
 * The LA Mirage mobile app's store identity — one place for lamirage.co and la-mirage.in
 * (spec docs/superpowers/specs/2026-10-07-lamirage-co-landing-app-launch-design.md).
 *
 * Gating is PER STORE. Each flag controls that store's badge, store link, JSON-LD entry and (for
 * iOS) the apple-itunes-app smart banner:
 *
 * - PLAY_STORE_LIVE — Google Play published the app 2026-10-08 (the listing returns 200). Google's
 *   badge guidelines only require the app to be live on Play, so the badge ships now.
 * - IOS_APP_STORE_LIVE — Apple approved the app 2026-10-31 (Ready for Sale). Apple's badge guidelines
 *   forbid the App Store badge before the app is available, so this was flipped only once the App
 *   Store URL below resolved publicly.
 *
 * APP_STORES_LIVE still means "BOTH stores list the app" — derived, never set by hand.
 */
export const PLAY_STORE_LIVE = true;
export const IOS_APP_STORE_LIVE = true;
export const APP_STORES_LIVE = PLAY_STORE_LIVE && IOS_APP_STORE_LIVE;
/** True when at least one store lists the app (MobileApplication JSON-LD ships). */
export const ANY_APP_STORE_LIVE = PLAY_STORE_LIVE || IOS_APP_STORE_LIVE;

export const APP_IOS_ID = "6817849739";
export const APP_ANDROID_PACKAGE = "in.lamirage.app";
export const APP_STORE_URL = `https://apps.apple.com/app/id${APP_IOS_ID}`;
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${APP_ANDROID_PACKAGE}`;

/** Owner chose "India's first" (2026-10-07); always shown with APP_FIRST_QUALIFIER nearby. */
export const APP_FIRST_CLAIM = "India's first wedding venue with its own app";
export const APP_FIRST_QUALIFIER = "as far as we know";
/** Neither store live. */
export const APP_COMING_SOON = "Coming soon to iPhone & Android";
/** Shown next to the Google Play badge while iOS is not live. */
export const IOS_COMING_SOON = "Coming soon to iPhone";
/** Shown next to the App Store badge while Play is not live (not expected, but symmetric). */
export const ANDROID_COMING_SOON = "Coming soon to Android";

export interface AppStoreStatus {
  play: boolean;
  ios: boolean;
}

/** Store entries (OS name + URL) for the stores that are live — iOS first, as the badges are. */
export function liveAppStores(
  s: AppStoreStatus = { play: PLAY_STORE_LIVE, ios: IOS_APP_STORE_LIVE },
): Array<{ os: "Android" | "iOS"; url: string }> {
  const out: Array<{ os: "Android" | "iOS"; url: string }> = [];
  if (s.ios) out.push({ os: "iOS", url: APP_STORE_URL });
  if (s.play) out.push({ os: "Android", url: PLAY_STORE_URL });
  return out;
}

/** The "coming soon" caption for whichever store is NOT live, or null when both are. */
export function appComingSoonLabel(
  s: AppStoreStatus = { play: PLAY_STORE_LIVE, ios: IOS_APP_STORE_LIVE },
): string | null {
  if (s.play && s.ios) return null;
  if (s.play) return IOS_COMING_SOON;
  if (s.ios) return ANDROID_COMING_SOON;
  return APP_COMING_SOON;
}

/** Eyebrow/kicker text above the app hero/band ("New · …"), following the live stores. */
export function appAvailabilityKicker(
  s: AppStoreStatus = { play: PLAY_STORE_LIVE, ios: IOS_APP_STORE_LIVE },
): string {
  if (s.play && !s.ios) return "New · Android now · iPhone soon";
  if (s.ios && !s.play) return "New · iPhone now · Android soon";
  return "New · iPhone & Android";
}
